/**
 * SidebarRuntimeStatusDot — small status dot driven by ForgeRuntimeStatus.
 *
 * Color mapping (design tokens only):
 *   registered   → hollow ring (border-muted-foreground/40) — no VM exists yet
 *   running      → green (bg-success)
 *   provisioning → amber pulse (bg-warning animate-status-pulse)
 *   starting     → amber pulse (bg-warning animate-status-pulse)
 *   stopped      → grey (bg-muted-foreground/40) — holds a warm-pool slot
 *   draining     → grey (bg-muted-foreground/40) — holds a warm-pool slot
 *   error        → red pulse (bg-destructive animate-status-pulse)
 *   destroyed    → HOLLOW ring, same as `registered` — frees the slot
 *
 * `registered` is deliberately a HOLLOW ring rather than a filled grey dot: a
 * filled dot reads as "a machine that is off", and for a registered project
 * there is no machine at all. Nothing is wrong and nothing is running.
 *
 * Forge Cloud runtimes are per-(org, user, project) with a hard org-level cap
 * on warm slots: Stop = suspend (still holds a slot), Recycle = same machine
 * (still holds a slot), Destroy = the ONLY action that frees a slot. `stopped`
 * is labeled "Suspended" rather than "Stopped" so it does not read as "off,
 * costing nothing" — it still counts against the cap. `destroyed` shares the
 * hollow `registered` treatment for the same reason: no machine, no slot held.
 *
 * This is a SEPARATE element from SidebarLinkStateDot.
 * SidebarLinkStateDot shows location (cloud/monitor glyph).
 * SidebarRuntimeStatusDot shows "is this VM alive" for cloud rows.
 */

import React from "react";
import type { ForgeRuntimeStatus } from "../../cmmd/forgeRuntimeStatus";
import { runtimeStatusToDotVariant } from "./cloudRuntimeRowState.logic";
import { Tooltip, TooltipPopup, TooltipTrigger } from "../ui/tooltip";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface SidebarRuntimeStatusDotProps {
  status: ForgeRuntimeStatus;
  /** When false, suppresses the tooltip wrapper. Default: true. */
  withTooltip?: boolean;
  className?: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * A Record rather than a switch: keyed on ForgeRuntimeStatus, TypeScript refuses
 * to compile a missing member, so this is STRICTER than the `default:` +
 * `never` switch it replaces — that one only failed once a value reached the
 * default arm. It is also cyclomatic 1; the switch crossed the repo's
 * complexity gate as soon as `suspended` was added.
 */
const STATUS_LABELS: Readonly<Record<ForgeRuntimeStatus, string>> = {
  // Never "Stopped"/"Error": the workspace has not been created yet, which is
  // the expected state for a freshly imported project.
  registered: "Project not started",
  running: "Running",
  provisioning: "Provisioning",
  starting: "Starting",
  // "Suspended", not "Stopped": Stop still HOLDS a Forge Cloud warm-pool slot
  // until the workspace is Destroyed. "Stopped" reads as "off, costing
  // nothing", which is false here.
  stopped: "Suspended",
  // Indistinguishable to a user from `stopped`: both hold a warm-pool slot and
  // both come back on use. The distinction between them is internal (only
  // `suspended` is auto-connectable), so two labels would be noise.
  suspended: "Suspended",
  // "Updating", not "Draining": draining is CMMD's internal migration state
  // (moving to a new image), not a user-facing failure or teardown — and it
  // still holds a slot, same as Suspended.
  draining: "Updating",
  error: "Runtime error",
  destroyed: "Destroyed",
};

function statusLabel(status: ForgeRuntimeStatus): string {
  return STATUS_LABELS[status] ?? "Unknown";
}

/**
 * Richer tooltip copy for statuses whose one-word label needs context. Falls
 * back to the same text as `statusLabel` for every other status so the
 * tooltip and the sr-only/aria-label text agree everywhere except here.
 *
 * `stopped`/`draining` spell out "keeps your slot" — the SAME vocabulary the
 * Settings row actions use (`CloudRuntimeExecutionRow`: "Suspend workspace
 * (keeps your slot)" / "Destroy workspace (frees your slot)") — so a user
 * reads the same fact whether they hover the sidebar dot or the Settings row.
 */
function statusTooltipDescription(status: ForgeRuntimeStatus): string {
  switch (status) {
    case "stopped":
      return "Suspended — keeps your Forge Cloud slot until Destroyed.";
    case "draining":
      return "Forge is applying a project update. Your files and threads are safe. Keeps your Forge Cloud slot until Destroyed.";
    default:
      return statusLabel(status);
  }
}

function dotClasses(status: ForgeRuntimeStatus): string {
  const variant = runtimeStatusToDotVariant(status);
  switch (variant) {
    case "green":
      // Same semantic "online" state as the settings runtime row
      // (CloudExecutionTargetsSection) — matches its token, not bg-success.
      return "bg-success";
    case "amber-pulse":
      // `animate-status-pulse`, not Tailwind's `animate-pulse`: this dot is on
      // screen for the entire time a workspace is coming up, and the stock
      // smooth curve recomposites every vsync for all of it.
      return "bg-warning motion-safe:animate-status-pulse";
    case "grey":
      return "bg-muted-foreground/40";
    case "red-pulse":
      // Semantic destructive/error token, not a hardcoded Tailwind red.
      return "bg-destructive motion-safe:animate-status-pulse";
    case "idle":
      return "border border-muted-foreground/40 bg-transparent";
    case "hidden":
      return "hidden";
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function SidebarRuntimeStatusDot({
  status,
  withTooltip = true,
  className,
}: SidebarRuntimeStatusDotProps) {
  const variant = runtimeStatusToDotVariant(status);
  if (variant === "hidden") return null;

  const label = statusLabel(status);
  const dotSpan = (
    <span
      aria-label={label}
      className={`inline-flex size-2.5 shrink-0 items-center justify-center${className ? ` ${className}` : ""}`}
    >
      <span className={`size-1 rounded-full ${dotClasses(status)}`} />
      <span className="sr-only">{label}</span>
    </span>
  );

  if (!withTooltip) {
    return dotSpan;
  }

  return (
    <Tooltip>
      <TooltipTrigger render={dotSpan} />
      <TooltipPopup side="top">{statusTooltipDescription(status)}</TooltipPopup>
    </Tooltip>
  );
}
